import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import useToggle from 'hooks/useToggle'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import { rem } from 'polished'
import styled from 'styled-components'

const stringable = new Set(['boolean', 'number', 'undefined'])
const toStringable = new Set(['symbol', 'function'])

const ToggleClickable = styled(Clickable)`
  position: absolute;
  inset-inline-start: ${rem(-20)};
  top: ${rem(1)};
  overflow: hidden;

  & svg {
    transform: rotate(-90deg);
    transition: transform 0.2s;
    margin-block: ${rem(-4)};
  }

  &.isExpanded svg {
    transform: rotate(0);
  }
`

const MonospaceTextBlock = styled.div`
  font-family: monospace;
  padding-inline-start: 2ch;
  position: relative;
  text-indent: -2ch;
  word-break: break-word;
`

const ObjKey = styled.span`
  font-weight: bold;
`

interface Props {
  value: any
  objKey?: string
  initialExpanded?: true
}

function ExpandableObjectView({ value, objKey, initialExpanded }: Props) {
  const { value: isExpanded, toggle: toggleExpanded } = useToggle(initialExpanded)

  const keyPrefix = objKey ? <ObjKey>{objKey}: </ObjKey> : ''

  const toggleButton = <ToggleClickable onClick={toggleExpanded} className={clsx({ isExpanded })}>
    <LineAngleDownIcon size={20} colour="light-foreground-alt" />
  </ToggleClickable>

  if (value === null || stringable.has(typeof value)) {
    return <MonospaceTextBlock>
      {keyPrefix}{String(value)}
    </MonospaceTextBlock>
  }

  if (toStringable.has(typeof value)) {
    return <MonospaceTextBlock>
      {keyPrefix}{value.toString()}
    </MonospaceTextBlock>
  }

  if (typeof value === 'string') {
    return <MonospaceTextBlock>
      {keyPrefix}{JSON.stringify(value)}
    </MonospaceTextBlock>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <MonospaceTextBlock>
        {keyPrefix}[ ]
      </MonospaceTextBlock>
    }

    if (!isExpanded) {
      return <MonospaceTextBlock>
        {toggleButton}
        <div>{keyPrefix}[ ... ]</div>
      </MonospaceTextBlock>
    }

    return <MonospaceTextBlock>
      {toggleButton}
      <div>{keyPrefix}[</div>
      {value.map((childValue, i) => (
        <ExpandableObjectView key={i} objKey={`${i}`} value={childValue} />
      ))}
      <div>]</div>
    </MonospaceTextBlock>
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value)

    if (entries.length === 0) {
      return <MonospaceTextBlock>
        {keyPrefix}{'{ }'}
      </MonospaceTextBlock>
    }

    if (!isExpanded) {
      return <MonospaceTextBlock>
        {toggleButton}
        <div>{keyPrefix}{'{ ... }'}</div>
      </MonospaceTextBlock>
    }

    return <MonospaceTextBlock>
      {toggleButton}
      <div>{keyPrefix}{'{'}</div>
      {entries.map(([key, childValue]) => (
        <ExpandableObjectView key={key} objKey={key} value={childValue} />
      ))}
      <div>{'}'}</div>
    </MonospaceTextBlock>
  }

  return <MonospaceTextBlock>{keyPrefix}unknown type</MonospaceTextBlock>
}

export default ExpandableObjectView
